/** @type {import('tailwindcss').Config} */
module.exports = {
  content: [
    "./src/pages/**/*.{js,ts,jsx,tsx,mdx}",
    "./src/components/**/*.{js,ts,jsx,tsx,mdx}",
    "./src/app/**/*.{js,ts,jsx,tsx,mdx}",
  ],
  theme: {
    extend: {
      backgroundImage: {
        "gradient-radial": "radial-gradient(var(--tw-gradient-stops))",
        "gradient-conic":
          "conic-gradient(from 180deg at 50% 50%, var(--tw-gradient-stops))",
      },
      colors: {
        primary: "#80578a",
        secondary: "#80578a",
        primaryLight: "#e8deea",
        // The TTV editor's neutral ramp. Fully achromatic on purpose: the editor is the app's first dark-by-default surface, and every hue on screen is meant to belong to content — a singer's notes, an armed state — so the chrome carries none. Steps are ~5 points of CIE L* apart. Levels encode DEPTH, not identity: two things at the same depth share a value however different their jobs, and surfaces are told apart by their value alone rather than by a drawn divider.
        ttvWell: "#161616",
        // The frame: the top toolbar, the hint bar, the bottom strip, and the ring behind a stacked singer chip. Darker than the grid so the chrome sits back and the work sits forward, but not near-black: on black the ramp's ~5 L* steps become a much larger RELATIVE jump, so every boundary in the editor hardens at once and the thing stops reading as one surface with depth in it. Going that dark takes a compression of the whole ramp to much smaller steps, not a move of this one value.
        ttvGround: "#191919",
        // The grid's own lit row, mirrored from COLORS.SLATE_GRAY on the canvas side so the two cannot drift. Named so the DOM panels beside the roll, like the mixer, can match it: at ttvSurface, a full 4.3 L* above the grid, a panel reads as a lighter slab bolted to the editor rather than as its neighbour.
        ttvGrid: "#1f1f1f",
        ttvSurface: "#282828",
        ttvRaised: "#333333",
        // The backing clip's body: the same tone as the phoneme boxes in the lane above it (COLORS.VERY_DARK_BLUE), so the two strips under the roll hold their content in one kind of block. Neutral like them: a blue cast makes it the one hue in the editor's chrome and the loudest thing under the phoneme strip.
        ttvClip: "#282828",
        ttvClipHover: "#303030",
        // A problem worth noticing, and the only hue in the editor's chrome. Not raw amber-300: at L* 85.9, unstyled yellow type sitting loose in the toolbar makes the save area read as a developer's console rather than part of the product. The ink sits at 72 so it is still the loudest thing in that row without being the loudest on screen, and the fill is a rung of the ramp with the same hue in it, so the warning wears the pill shape Save and Export already wear.
        ttvWarn: "#d4aa5b",
        ttvWarnWell: "#3b2e1f",
        // A hovered control, +8.0 L* over the pod it sits in: enough to read as a lift while staying under the armed rung. An explicit tone rather than a white wash, because a wash lands differently on a resting control than on an armed one and the hover would not mean the same thing in both. This value is a STEP over the ttvGrid pods, so it tracks whatever they sit on rather than holding a number — #454545 would flash +17.6 over them.
        ttvHover: "#303030",
        // The library window's secondary ink — subtitles, the resting rail, the search placeholder. L* 66, between ttvUnit and ttvText; at ttvUnit's 58 the window's second line reads as smaller than it is.
        ttvSubtle: "#a1a1a1",
        // A toggled-ON control. Its own rung above ttvHover, so a lit control never reads as a hovered one, and one rung only: a mode toggle should not be the brightest thing in the toolbar.
        ttvArmed: "#3e3e3e",
        // The one border left in the editor, for outlines that have to read against a surface rather than separate two of them. The floating layer's whole recipe, since twenty-two boxes hang over the roll and, without one, wore four surfaces, four edges and three radii between them: bg-ttvCard, NO border, rounded-xl, shadow-floatingCard, and padding by KIND — p-1.5 when the box is a list of rows that carry their own padding, p-3 when it is a card with content in it. An inline select under ~28px tall keeps rounded-md; twelve pixels of radius on a box that short reads as a lozenge. What goes INSIDE one, which had spread just as far — six text sizes, seven row heights, four icon sizes: text is 10px for a caption or an eyebrow, 11px for a row's own label, 12px for a paragraph of prose, and nothing else; a row you click is 28px tall and a chip inside a row is 24px, with a taller row only when it carries something that is not text (a voice card's thumbnail, the scale grid's key buttons); icons are 14px beside 11px type and 16px beside 12px. The boxes that live inside a DIALOG follow that dialog's form scale instead — their selects drop from 14px fields and would change size on picking otherwise.
        ttvLine: "#434343",
        // Every floating card that holds controls — menus, rack cards, pickers. Borderless: a hairline round a dark card reads like an old operating system's window, so the card is told apart from the busy roll by its soft shadow-floatingCard, on the editor's one raised step, the same tone as ttvSurface. A rung lighter, at #333333, it reads as a grey sheet laid on the editor rather than as part of it. Rows inside it hover to ttvArmed. Every dialog window wears it too, so a window and a card are one family; the controls inside a window step UP from it to ttvRaised, borderless, rather than sinking into near-black wells, which read as dated.
        ttvCard: "#282828",
        // Three ink levels, spread wide inside one row (ttvValue at L* 95, ttvText at 76, ttvUnit at 58) so brightness carries the hierarchy rather than WEIGHT: with almost everything at one level, weight has to separate things instead, and this way the editor can hold to two weights. Every further tone is a decision the reader has to make, and `bright` (#f7f7f7) is reserved for content, the pitch line and lyrics, rather than for labels.
        ttvValue: "#f1f1f1",
        ttvText: "#bcbcbc",
        ttvUnit: "#8c8c8c",
        ttvDim: "#848484",
        // The Black Friday banner's gradient (BlackFridayBanner.tsx): dark plum at both ends, lighter plum in the middle. Named so the banner does not need arbitrary hex classes.
        blackFridayDark: "#2d1538",
        blackFridayMid: "#6b4c7a",
        // The editor's shell tone from before the ttv* ramp, still used by the vocal drop overlay (VocalDropTarget). Not part of the ramp: COLORS.VERY_DARK_BLUE, which it once matched, now holds a ramp value.
        darkBlue: "#121820",
        // The TTV roster, all six. Must stay in lockstep with SINGER_COLORS in the TTV constants, which Konva uses to draw the same singers on canvas — these are the DOM half of one palette and the two are only kept in step by hand, so an edit to one without the other lets them drift apart. `singer1` exists rather than reusing `primary`: with the brand token as slot one, a one-singer project has its content, its logo and its focus ring all in the same purple.
        singer1: "#6a4877",
        singer2: "#046084",
        singer3: "#867034",
        singer4: "#436740",
        singer5: "#945136",
        singer6: "#862942",
      },
      keyframes: {
        // The Text to vocals checking screen's spinner (TtvAccessSpinner): the arc grows into a long sweep, then its tail catches up, while the whole ring turns. Lengths are on the spinner's r=20 circle (circumference ~126), so 90 is about three quarters of the ring.
        spinnerArc: {
          "0%": { strokeDasharray: "1, 150", strokeDashoffset: "0" },
          "50%": { strokeDasharray: "90, 150", strokeDashoffset: "-35" },
          "100%": { strokeDasharray: "90, 150", strokeDashoffset: "-124" },
        },
        // The access screens' swollen glow fading in over the resting one and back out (TtvGateGlow), so the light breathes without anything being resized.
        glowSwell: {
          "0%, 100%": { opacity: "0" },
          "50%": { opacity: "1" },
        },
        spinnerTurn: {
          to: { transform: "rotate(360deg)" },
        },
        ellipsis: {
          "0%, 80%, 100%": { transform: "scale(0)" },
          "40%": { transform: "scale(1.0)" },
        },
        fadeIn: {
          from: { opacity: "0" },
          to: { opacity: "1" },
        },
        fadeOut: {
          from: { opacity: "1" },
          to: { opacity: "0" },
        },
        // A bar a third as wide as its track, sliding from just off the left edge to just off the right.
        progressSlide: {
          from: { transform: "translateX(-100%)" },
          to: { transform: "translateX(300%)" },
        },
      },
      animation: {
        // "Still working" for waits with no percentage to show (the vocal import). Plain CSS on purpose: a transform animation keeps moving while the page's JavaScript is busy, which is exactly when the user needs to see it move.
        "progress-slide": "progressSlide 1.6s ease-in-out infinite",
        ellipsis: "ellipsis 1.4s infinite ease-in-out both",
        // The note/group text editor's enter/exit fade. Duration must match TEXT_EDITOR_EXIT_ANIMATION_MS in constants.ts (fade-out specifically: that is how long the box is kept mounted after closing, so this animation has time to finish before the DOM node is removed).
        "fade-in": "fadeIn 200ms ease-out",
        "fade-out": "fadeOut 200ms ease-in forwards",
        // Two clocks that never line up, so the arc lands somewhere new on every loop instead of repeating one spot.
        "spinner-arc": "spinnerArc 1.5s ease-in-out infinite",
        "spinner-turn": "spinnerTurn 2s linear infinite",
        "glow-swell": "glowSwell 10s ease-in-out infinite",
      },
      boxShadow: {
        // The floating card's only separation from the roll now that it has no border: soft, so it lifts off notes and gridlines rather than ending at a hard edge. Kept light — at 85% black and 56px of blur it sits on the editor as a dark smudge.
        // Named apart from the ttvCard COLOUR on purpose: a shadow key that matches a colour name makes shadow-ttvCard set the shadow's colour to that grey, which draws a light glow round every card instead of a shadow.
        floatingCard:
          "0 12px 32px -6px rgba(0, 0, 0, 0.5), 0 3px 8px rgba(0, 0, 0, 0.3)",
      },
      dropShadow: {
        strong: "0px 1px 2px rgba(0, 0, 0, 0.35)", // For making sure objects are visible on white backgrounds
        // The Text to vocals checking spinner's halo: primaryLight (#e8deea) at partial strength, so the arc glows on the dark ground without blooming into the headline.
        spinnerGlow: "0 0 10px rgba(232, 222, 234, 0.45)",
      },
    },
  },
  plugins: [
    function ({ addVariant }) {
      addVariant("fullscreen", "&:fullscreen");
    },
    // `text-balance`: evens out a short block's line lengths so it never ends on a lone word. Built into Tailwind from 3.4; this project is on 3.3, where the class silently does nothing. Drop this once Tailwind is upgraded.
    function ({ addUtilities }) {
      addUtilities({ ".text-balance": { "text-wrap": "balance" } });
    },
    // `scrollbar-thin`: a slim, track-less, translucent scrollbar for DOM scroll areas inside the dark editor (the mixer/effects panel first). The OS default on Windows is a 17px light-grey gutter with arrow buttons, which reads as a foreign object against the neutral-900 chrome. Firefox takes the standard properties; Chromium/Safari the ::-webkit-scrollbar pseudo-elements. Thumb hues are white at low alpha so they sit on any of the editor's dark surfaces.
    function ({ addUtilities }) {
      addUtilities({
        ".scrollbar-thin": {
          "scrollbar-width": "thin",
          "scrollbar-color": "rgba(255, 255, 255, 0.16) transparent",
          "&::-webkit-scrollbar": { width: "6px", height: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            "background-color": "rgba(255, 255, 255, 0.16)",
            "border-radius": "9999px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            "background-color": "rgba(255, 255, 255, 0.28)",
          },
          "&::-webkit-scrollbar-button": { display: "none" },
        },
        // `scrollbar-none`: a scroll area whose height is too small to share with a scrollbar. Where the OS draws classic scrollbars, a visible one inside a 24px bar would take most of its height from the controls; the area still scrolls by trackpad and shift-wheel.
        ".scrollbar-none": {
          "scrollbar-width": "none",
          "&::-webkit-scrollbar": { display: "none" },
        },
      });
    },
    require("@tailwindcss/forms"),
    require("@tailwindcss/aspect-ratio"),
  ],
  safelist: [
    "w-[0%]",
    "w-[1%]",
    "w-[2%]",
    "w-[3%]",
    "w-[4%]",
    "w-[5%]",
    "w-[6%]",
    "w-[7%]",
    "w-[8%]",
    "w-[9%]",
    "w-[10%]",
    "w-[11%]",
    "w-[12%]",
    "w-[13%]",
    "w-[14%]",
    "w-[15%]",
    "w-[16%]",
    "w-[17%]",
    "w-[18%]",
    "w-[19%]",
    "w-[20%]",
    "w-[21%]",
    "w-[22%]",
    "w-[23%]",
    "w-[24%]",
    "w-[25%]",
    "w-[26%]",
    "w-[27%]",
    "w-[28%]",
    "w-[29%]",
    "w-[30%]",
    "w-[31%]",
    "w-[32%]",
    "w-[33%]",
    "w-[34%]",
    "w-[35%]",
    "w-[36%]",
    "w-[37%]",
    "w-[38%]",
    "w-[39%]",
    "w-[40%]",
    "w-[41%]",
    "w-[42%]",
    "w-[43%]",
    "w-[44%]",
    "w-[45%]",
    "w-[46%]",
    "w-[47%]",
    "w-[48%]",
    "w-[49%]",
    "w-[50%]",
    "w-[51%]",
    "w-[52%]",
    "w-[53%]",
    "w-[54%]",
    "w-[55%]",
    "w-[56%]",
    "w-[57%]",
    "w-[58%]",
    "w-[59%]",
    "w-[60%]",
    "w-[61%]",
    "w-[62%]",
    "w-[63%]",
    "w-[64%]",
    "w-[65%]",
    "w-[66%]",
    "w-[67%]",
    "w-[68%]",
    "w-[69%]",
    "w-[70%]",
    "w-[71%]",
    "w-[72%]",
    "w-[73%]",
    "w-[74%]",
    "w-[75%]",
    "w-[76%]",
    "w-[77%]",
    "w-[78%]",
    "w-[79%]",
    "w-[80%]",
    "w-[81%]",
    "w-[82%]",
    "w-[83%]",
    "w-[84%]",
    "w-[85%]",
    "w-[86%]",
    "w-[87%]",
    "w-[88%]",
    "w-[89%]",
    "w-[90%]",
    "w-[91%]",
    "w-[92%]",
    "w-[93%]",
    "w-[94%]",
    "w-[95%]",
    "w-[96%]",
    "w-[97%]",
    "w-[98%]",
    "w-[99%]",
    "w-[100%]",
    ...Array.from({ length: 1501 }, (_, i) => `w-[${i}px]`),
    ...Array.from({ length: 1501 }, (_, i) => `h-[${i}px]`),
  ],
};
